import React, { FC, useState, useEffect } from 'react';
import { Card, Typography, Box, LinearProgress, TextField, Button, IconButton, Alert, CardContent, CardActions, CardMedia } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import clientService from 'src/clientService';
import axios from 'axios';
import { ProjectMediaUploadRequest, ProjectMediaUploadResponse, ProjectMediaFinalizeRequest, MediaType, ProjectMedia } from 'src/api/Api';

interface UploadPreviewCardProps {
  file: File;
  projectId: string;
  onRemove: (file: File) => void;
  onUploadComplete: (fileName: string) => void;
  onFinalize: (mediaItem: ProjectMedia) => void;
}

const UploadPreviewCard: FC<UploadPreviewCardProps> = ({ file, projectId, onRemove, onUploadComplete, onFinalize }) => {
  const [caption, setCaption] = useState('');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);
  const [gcsPath, setGcsPath] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    // Create a preview URL for the file
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    // Cleanup the object URL on component unmount
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  useEffect(() => {
    const uploadFile = async () => {
      setIsUploading(true);
      setError(null);

      try {
        const mediaType = file.type.startsWith('image') ? MediaType.IMAGE : file.type.startsWith('video') ? MediaType.VIDEO : MediaType.AUDIO;

        const generateUrlResponse = await clientService.api.generateProjectMediaUploadUrlApiV1AdminProjectMediaGenerateUploadUrlPost({
          project_id: projectId,
          file_name: file.name,
          content_type: file.type,
          media_type: mediaType,
        } as ProjectMediaUploadRequest);

        const { upload_url, gcs_path } = generateUrlResponse.data as ProjectMediaUploadResponse;
        setGcsPath(gcs_path);

        await axios.put(upload_url, file, {
          headers: { 'Content-Type': file.type },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
            setProgress(percentCompleted);
          },
        });

        setIsUploaded(true);
        onUploadComplete(file.name);
      } catch (err) {
        console.error('Upload failed', err);
        setError('Upload failed. Please try again.');
      } finally {
        setIsUploading(false);
      }
    };

    if (!isUploading && !isUploaded) {
        uploadFile();
    }
  }, [file, projectId, onUploadComplete, isUploading, isUploaded]);

  const handleSave = async () => {
    if (!gcsPath) return;

    try {
      const mediaType = file.type.startsWith('image') ? MediaType.IMAGE : file.type.startsWith('video') ? MediaType.VIDEO : MediaType.AUDIO;

      const response = await clientService.api.finalizeProjectMediaUploadApiV1AdminProjectMediaFinalizeUploadPost({
        project_id: projectId,
        file_name: file.name,
        gcs_path: gcsPath,
        caption: caption,
        media_type: mediaType,
      } as ProjectMediaFinalizeRequest);
      
      onFinalize(response.data);

    } catch (err) {
      console.error('Finalize upload failed', err);
      setError('Failed to save caption.');
    }
  };

  const renderPreview = () => {
    if (!previewUrl) return null;
    const fileType = file.type.split('/')[0];

    if (fileType === 'image') {
      return <CardMedia component="img" height="140" image={previewUrl} alt={file.name} />;
    } else if (fileType === 'video') {
      return <CardMedia component="video" height="140" src={previewUrl} controls />;
    } else if (fileType === 'audio') {
        return (
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 140, bgcolor: 'grey.200' }}>
                <Typography variant="h6">Audio File</Typography>
                <audio src={previewUrl} controls style={{ width: '100%', marginTop: '10px' }} />
            </Box>
        );
    }
    return (
        <Box sx={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.200' }}>
            <Typography>Unsupported File</Typography>
        </Box>
    );
  };

  return (
    <Card>
      {renderPreview()}
      <CardContent>
        <Typography variant="subtitle2" noWrap title={file.name}>
          {file.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {Math.round(file.size / 1024)} KB
        </Typography>

        {(isUploading || (isUploaded && progress < 100)) && (
          <Box sx={{ mt: 1 }}>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
        )}
        {isUploaded && progress === 100 && <Alert severity="success" sx={{ mt: 1 }}>Upload complete!</Alert>}
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}

        <TextField
          fullWidth
          label="Caption"
          variant="outlined"
          size="small"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          sx={{ mt: 2 }}
          disabled={isUploading || !isUploaded}
        />
      </CardContent>
      <CardActions sx={{ justifyContent: 'space-between' }}>
        <IconButton size="small" onClick={() => onRemove(file)} disabled={isUploading}>
            <CloseIcon />
        </IconButton>
        <Button
          variant="contained"
          size="small"
          disabled={isUploading || !isUploaded || !caption}
          onClick={handleSave}
        >
          Save
        </Button>
      </CardActions>
    </Card>
  );
};

export default UploadPreviewCard;
